import { useEffect, useMemo, useState } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useAuth, useUser } from "@clerk/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useOrganizationSettingsStore } from "@/stores";
import type { SavedCustomField } from "@/types/types";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  SwitchCard,
  SwitchCardSwitch,
  SwitchCardContent,
  SwitchCardLabel,
  SwitchCardDescription,
} from "@/components/ui/switch-card";
import { Button } from "@/components/ui/button";
import {
  CampaignFormData,
  CustomField,
} from "@/components/dashboard_create_campaign/campaignFormTypes";
import AddCustomFieldDialog from "@/components/dashboard_create_campaign/AddCustomFieldDialog";
import { CREATOR_PROFILE_VARS, extractFormulaVariables } from "@/components/dashboard_create_campaign/formulaUtils";
import { BookmarkIcon, PencilIcon, PlusCircleIcon, Trash2Icon } from "lucide-react";

interface CampaignFormStep3Props {
  form: UseFormReturn<CampaignFormData>;
  loading: boolean;
}

export default function CampaignFormStep3({
  form,
  loading,
}: CampaignFormStep3Props) {
  const { fields, append, remove, update } = useFieldArray<CampaignFormData, "fields", "id">({
    control: form.control,
    name: "fields",
  });

  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const editingField = editingFieldIndex !== null ? fields[editingFieldIndex] : null;

  // Org saved-fields library (Settings → Saved Fields) for quick-adding.
  // Adding copies the definition into THIS campaign only — later edits here
  // never touch the library.
  const { organizationInfo, fetchOrganizationInfo } = useOrganizationSettingsStore();
  const { orgId, getToken } = useAuth();
  const { user } = useUser();
  useEffect(() => {
    if (!organizationInfo && orgId && user?.id) {
      void (async () => {
        const token = await getToken();
        if (token) await fetchOrganizationInfo(orgId, user.id, token);
      })();
    }
  }, [organizationInfo, orgId, user?.id, getToken, fetchOrganizationInfo]);
  const savedFields =
    (organizationInfo?.custom_field_library as SavedCustomField[] | undefined) ?? [];

  const [suggestingVariable, setSuggestingVariable] = useState<string | null>(null);

  // Scan all tiers for custom_formula components to compute suggested variables
  const tiers = form.watch("tiers");

  const suggestedVariables = useMemo(() => {
    const allSuggested: string[] = [];

    for (const tier of tiers ?? []) {
      for (const comp of tier.components ?? []) {
        if (comp.type === "custom_formula" && comp.custom_formula?.expression) {
          const allVars = extractFormulaVariables(comp.custom_formula.expression);
          const constantNames = (comp.custom_formula.constants ?? []).map((c) => c.name);
          const currentFieldNames = fields.map((f) => f.name || "");

          const unresolved = allVars.filter(
            (v) =>
              !(CREATOR_PROFILE_VARS as readonly string[]).includes(v) &&
              !constantNames.includes(v) &&
              !currentFieldNames.includes(v) &&
              !allSuggested.includes(v)
          );
          allSuggested.push(...unresolved);
        }
      }
    }

    return allSuggested;
  }, [tiers, fields]);

  const handleAddCustomField = (name: string, description: string, type: "string" | "boolean" | "number" | "date", defaultValue?: number) => {
    append({
      name,
      description,
      enabled: true,
      isCustom: true,
      type,
      ...(defaultValue !== undefined && { default_value: defaultValue }),
    });
  };

  const handleDeleteField = (index: number) => {
    remove(index);
  };

  const handleEditField = (index: number) => {
    setEditingFieldIndex(index);
  };

  const handleEditFieldSave = (
    name: string,
    description: string,
    type: "string" | "boolean" | "number" | "date",
    defaultValue?: number
  ) => {
    if (editingFieldIndex === null) return;
    update(editingFieldIndex, {
      ...fields[editingFieldIndex],
      name,
      description,
      type,
      default_value: defaultValue,
    });
    setEditingFieldIndex(null);
  };

  const existingFieldNames = fields.map((f) => f.name || "");
  const defaultFields = fields.filter((f) => !f.isCustom);
  const customFields = fields.filter((f) => f.isCustom);

  const renderField = (item: CustomField & { id: string }) => {
    const id = `field-${item.id}`;
    const field = item;
    const actualIndex = fields.findIndex((f) => f.id === item.id);

    return (
      <div key={item.id} className="space-y-2">
        <div className="relative">
          <SwitchCard
            tabIndex={-1}
            disabled={loading}
            className="focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
          >
            <SwitchCardSwitch
              id={id}
              checked={field.enabled || false}
              onCheckedChange={(checked: boolean) => {
                const newField = {
                  ...fields[actualIndex],
                  enabled: checked,
                };

                if (field.name === "Billing-Info" && !checked) {
                  newField.explanation = "";
                }

                update(actualIndex, newField);
              }}
              aria-describedby={`${id}-description`}
            />
            <SwitchCardContent>
              <SwitchCardLabel htmlFor={id}>
                {field.name || "Field Name"}{" "}
                <span className="text-muted-foreground text-xs leading-[inherit] font-normal">
                  ({field.type === "string" ? "Text" : field.type === "number" ? "Number" : field.type === "date" ? "Date" : "Yes/No"}
                  {field.negotiation && ", Negotiable"})
                </span>
              </SwitchCardLabel>
              <SwitchCardDescription
                id={`${id}-description`}
                className="truncate"
              >
                {field.description}
              </SwitchCardDescription>
            </SwitchCardContent>
          </SwitchCard>
          {field.isCustom && (
            <div className="bg-sidebar rounded-b-lg p-1 pt-1.5 mx-auto w-[98%] -mt-0.5 border border-t-0 flex gap-1">
              <Button
                type="button"
                variant="destructive-ghost"
                onClick={() => handleDeleteField(actualIndex)}
                disabled={loading}
                size="sm"
                className="h-7.5 px-2 gap-1.5 rounded-sm"
                scope={false}
              >
                <Trash2Icon className="text-destructive" /> Delete
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleEditField(actualIndex)}
                disabled={loading}
                size="sm"
                className="h-7.5 px-2 gap-1.5 ml-auto rounded-sm"
                scope={false}
              >
                <PencilIcon className="text-muted-foreground" /> Edit
              </Button>
            </div>
          )}
        </div>

        {/* Billing Explanation Field - Inside the billing field */}
        {field.name === "Billing-Info" && field.enabled && (
          <FormField
            disabled={loading}
            control={form.control}
            name={`fields.${actualIndex}.explanation`}
            render={({ field }) => (
              <FormItem className="pl-4 ml-2 border-l border-dashed">
                <FormLabel className="text-sm">
                  How do you pay creators? Be descriptive.
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="e.g. PayPal link, IBAN bank transfer"
                    className="min-h-20"
                    autoFocus
                    {...field}
                  />
                </FormControl>
                <FormDescription className="text-xs">
                  Amy will ask for that from the creator.
                </FormDescription>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Default CRM Fields Section */}
      <div className="space-y-2">
        <FormLabel className="font-semibold">CRM Fields</FormLabel>
        <FormDescription>
          Add CRM fields to collect specific information from creators. At
          least <span className="text-primary">one field must be enabled.</span>
        </FormDescription>
        <div className="flex flex-col gap-2 mt-5">
          {defaultFields.map((item) =>
            renderField(item as CustomField & { id: string })
          )}
        </div>
      </div>

      {suggestedVariables.length > 0 && (
        <div className="mt-8 space-y-1">
          <FormLabel className="text-sm">Formula Variables (Auto Generated)</FormLabel>
          <FormDescription className="text-xs">
            These variables from your formula need CRM fields to collect values from creators.
          </FormDescription>
          <div className="flex flex-wrap gap-2 mt-2">
            {suggestedVariables.map((v) => (
              <Button
                key={v}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSuggestingVariable(v)}
                className="font-mono border-dashed [&>svg]:text-muted-foreground"
              >
                <PlusCircleIcon className="size-3.5" /> {v}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Fields Section */}
      <div className="mt-8 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col items-start gap-1">
            <FormLabel className="font-semibold">Custom Fields</FormLabel>
            <FormDescription>
              Additional fields you've created to collect specific information.
            </FormDescription>
          </div>
          {savedFields.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="secondary" disabled={loading} size="sm">
                  <BookmarkIcon className="size-3.5 text-muted-foreground" />
                  Saved fields
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-1" align="start">
                <div className="flex max-h-72 flex-col gap-0.5 overflow-auto [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]">
                  {savedFields.map((sf) => {
                    const alreadyAdded = existingFieldNames.some(
                      (n) => n.toLowerCase() === sf.name.toLowerCase()
                    );
                    return (
                      <Button
                        key={sf.name}
                        type="button"
                        variant="ghost"
                        scope={false}
                        disabled={alreadyAdded}
                        className="h-auto w-full flex-col items-start gap-0 rounded-sm px-2.5 py-1.5 text-left transition-none"
                        onClick={() =>
                          handleAddCustomField(sf.name, sf.description, sf.type, sf.default_value)
                        }
                      >
                        <span className="text-sm font-medium">
                          {sf.name}{" "}
                          <span className="text-xs font-normal text-muted-foreground">
                            ({sf.type === "string" ? "Text" : sf.type === "number" ? "Number" : sf.type === "date" ? "Date" : "Yes/No"}
                            {alreadyAdded ? ", added" : ""})
                          </span>
                        </span>
                        <span className="w-full truncate text-xs font-normal text-muted-foreground">
                          {sf.description}
                        </span>
                      </Button>
                    );
                  })}
                </div>
                <p className="border-t px-2.5 py-1.5 mt-1 text-[11px] text-muted-foreground">
                  Adds a copy to this campaign — edit it freely, the saved
                  version stays unchanged.
                </p>
              </PopoverContent>
            </Popover>
          )}
        </div>
        {customFields?.length > 0 && <div className="flex flex-col gap-2 mt-5">
          {customFields.map((item) =>
            renderField(item as CustomField & { id: string })
          )}
        </div>}
      </div>

      <div>
        <AddCustomFieldDialog
          onAdd={handleAddCustomField}
          existingFieldNames={existingFieldNames}
          disabled={loading}
        />
        <AddCustomFieldDialog
          editMode
          externalOpen={editingFieldIndex !== null}
          onOpenChange={(open) => { if (!open) setEditingFieldIndex(null); }}
          editInitialValues={editingField ? {
            name: editingField.name ?? "",
            description: editingField.description ?? "",
            type: (editingField.type ?? "string") as "string" | "boolean" | "number" | "date",
            defaultValue: editingField.default_value,
          } : undefined}
          onEdit={handleEditFieldSave}
          onAdd={handleAddCustomField}
          existingFieldNames={existingFieldNames}
          disabled={loading}
        />
        <AddCustomFieldDialog
          externalOpen={suggestingVariable !== null}
          onOpenChange={(open) => { if (!open) setSuggestingVariable(null); }}
          prefillValues={suggestingVariable ? { name: suggestingVariable, type: "number" } : undefined}
          onAdd={(name, desc, type, defaultVal) => {
            handleAddCustomField(name, desc, type, defaultVal);
            setSuggestingVariable(null);
          }}
          existingFieldNames={existingFieldNames}
          disabled={loading}
        />
        <FormMessage className="text-xs mt-2">
          {form.formState.errors.fields?.message}
        </FormMessage>
        {form.formState.errors.fields?.root?.message && (
          <FormMessage className="text-xs mt-2">
            {form.formState.errors.fields.root.message}
          </FormMessage>
        )}
      </div>
    </div>
  );
}
